import { Routes } from '../../../../navigation/config/routes'
import {
    Campaign,
    CampaignStep,
    CampaignStepSchedule,
    CampaignStepType,
    CampaignSystemKind,
} from '../../../../models/Outreach/types'
import {
    DaySchedule,
    DelayStep,
    SequenceItem,
    SequenceStep,
    WeeklySchedule,
} from '../../../Outbound/Campaign/CreateCampaign/types'

const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const

const DAY_MAP: Record<string, number> = {
    sun: 0,
    mon: 1,
    tue: 2,
    wed: 3,
    thu: 4,
    fri: 5,
    sat: 6,
}

function defaultDaySchedule(): DaySchedule {
    return {
        enabled: false,
        startTime: '12:00 AM',
        endTime: '12:00 AM',
    }
}

function isDelayStep(item: SequenceItem): item is DelayStep {
    return item.type === 'delay'
}

function isMessageStep(item: SequenceItem): item is SequenceStep {
    return item.type === 'email' || item.type === 'sms'
}

export function scheduleToWeekly(
    schedule: CampaignStepSchedule[] | undefined,
): WeeklySchedule {
    const weekly: WeeklySchedule = {
        sun: defaultDaySchedule(),
        mon: defaultDaySchedule(),
        tue: defaultDaySchedule(),
        wed: defaultDaySchedule(),
        thu: defaultDaySchedule(),
        fri: defaultDaySchedule(),
        sat: defaultDaySchedule(),
    }

    for (const entry of schedule ?? []) {
        const key = DAY_KEYS[entry.day_of_week]
        if (key) {
            weekly[key] = {
                enabled: entry.enabled,
                startTime: entry.start_time,
                endTime: entry.end_time,
            }
        }
    }

    return weekly
}

export function campaignStepsToSequence(steps: CampaignStep[]): SequenceItem[] {
    const sorted = [...steps].sort((a, b) => a.step_order - b.step_order)

    return sorted.map((step) => {
        if (step.step_type === CampaignStepType.DELAY) {
            return {
                id: `step-${step.id ?? step.step_order}`,
                type: 'delay' as const,
                days: step.delay_days ?? 0,
            }
        }

        const type =
            step.step_type === CampaignStepType.EMAIL
                ? ('email' as const)
                : ('sms' as const)

        const guestCardId = step.guest_card?.id ?? null

        return {
            id: `step-${step.id ?? step.step_order}`,
            type,
            subject: step.subject ?? '',
            message: step.content ?? '',
            content_html: step.content_html ?? undefined,
            guest_card: guestCardId,
            schedule: scheduleToWeekly(step.schedule),
        }
    })
}

function scheduleToBackend(schedule: WeeklySchedule | undefined) {
    return schedule
        ? Object.entries(schedule).map(([day, daySchedule]) => ({
              day_of_week: DAY_MAP[day],
              enabled: daySchedule.enabled,
              start_time: daySchedule.startTime,
              end_time: daySchedule.endTime,
          }))
        : Object.values(DAY_MAP).map((dayOfWeek) => ({
              day_of_week: dayOfWeek,
              enabled: false,
              start_time: '12:00 AM',
              end_time: '12:00 AM',
          }))
}

/** Transform sequence items to backend PATCH steps, preserving existing step ids. */
export function sequenceToUpdateSteps(
    sequence: SequenceItem[],
    existingSteps: CampaignStep[],
) {
    const sortedExisting = [...existingSteps].sort(
        (a, b) => a.step_order - b.step_order,
    )

    return sequence.map((item, index) => {
        const stepOrder = index + 1
        const existing = sortedExisting[index]
        const id = existing?.id

        if (isDelayStep(item)) {
            return {
                ...(id != null ? { id } : {}),
                step_order: stepOrder,
                step_type: 'DELAY' as const,
                delay_days: item.days,
            }
        }

        const messageStep = item as SequenceStep
        const schedule = scheduleToBackend(messageStep.schedule)

        if (messageStep.type === 'email') {
            return {
                ...(id != null ? { id } : {}),
                step_order: stepOrder,
                step_type: 'EMAIL' as const,
                content: messageStep.message || '',
                content_html: messageStep.content_html || undefined,
                subject: messageStep.subject || '',
                schedule,
                guest_card: messageStep.guest_card || null,
            }
        }

        return {
            ...(id != null ? { id } : {}),
            step_order: stepOrder,
            step_type: 'TEXT_MESSAGE' as const,
            content: messageStep.message || '',
            schedule,
            guest_card: messageStep.guest_card || null,
        }
    })
}

export function channelsFromCampaign(
    campaign: Campaign | null | undefined,
): {
    email: boolean
    sms: boolean
} {
    if (!campaign) {
        return { email: true, sms: true }
    }

    if (campaign.system_kind === CampaignSystemKind.PRIMARY_EMAIL) {
        return { email: true, sms: false }
    }
    if (campaign.system_kind === CampaignSystemKind.PRIMARY_TEXT) {
        return { email: false, sms: true }
    }

    const hasEmail = campaign.steps?.some(
        (s) => s.step_type === CampaignStepType.EMAIL,
    )
    const hasSms = campaign.steps?.some(
        (s) => s.step_type === CampaignStepType.TEXT_MESSAGE,
    )

    if (hasEmail || hasSms) {
        return { email: !!hasEmail, sms: !!hasSms }
    }

    return { email: true, sms: true }
}

export function isDraftReplyTo(email: string): boolean {
    return email.trim().endsWith('@ezmanage.local')
}

export function isSeasonCampaignSetupComplete(
    name: string,
    replyToEmail: string,
    senderName: string,
    sequence: SequenceItem[],
): boolean {
    return (
        getSeasonCampaignActivationBlockers(
            name,
            replyToEmail,
            senderName,
            sequence,
        ).length === 0
    )
}

export function getSeasonCampaignActivationBlockers(
    name: string,
    replyToEmail: string,
    senderName: string,
    sequence: SequenceItem[],
): string[] {
    const blockers: string[] = []
    const messageSteps = sequence.filter(isMessageStep)

    if (name.trim() === '') {
        blockers.push('Campaign name is required.')
    }
    if (replyToEmail.trim() === '') {
        blockers.push('Reply-to email is required.')
    } else if (isDraftReplyTo(replyToEmail)) {
        blockers.push(
            'Replace the placeholder reply-to with your property\u2019s real leasing email.',
        )
    }
    if (senderName.trim() === '') {
        blockers.push('Sender name is required.')
    }
    if (messageSteps.length === 0) {
        blockers.push('Add at least one message step.')
    } else {
        const stepsWithoutGuestCard = messageSteps.filter(
            (step) => !step.guest_card,
        )
        if (stepsWithoutGuestCard.length > 0) {
            blockers.push(
                `Assign a guest card to all message steps (${stepsWithoutGuestCard.length} still missing).`,
            )
        }
    }

    return blockers
}

export type SeasonCampaignDetailTab =
    | 'setup'
    | 'sequence'
    | 'review'
    | 'tracking'
    | 'activity'

export const SEASON_CAMPAIGN_TAB_INDEX: Record<
    SeasonCampaignDetailTab,
    number
> = {
    setup: 0,
    sequence: 1,
    review: 2,
    tracking: 3,
    activity: 4,
}

const SEASON_CAMPAIGN_TAB_SLUG: Record<number, SeasonCampaignDetailTab> = {
    0: 'setup',
    1: 'sequence',
    2: 'review',
    3: 'tracking',
    4: 'activity',
}

export function parseSeasonCampaignTabFromSearch(search: string): number {
    const tab = new URLSearchParams(search).get('tab')
    if (!tab || !(tab in SEASON_CAMPAIGN_TAB_INDEX)) {
        return SEASON_CAMPAIGN_TAB_INDEX.setup
    }
    return SEASON_CAMPAIGN_TAB_INDEX[tab as SeasonCampaignDetailTab]
}

export function seasonCampaignTabSlugForIndex(
    index: number,
): SeasonCampaignDetailTab | null {
    return SEASON_CAMPAIGN_TAB_SLUG[index] ?? null
}

export function seasonCampaignDetailPath(
    campaignId: number | string,
    seasonId?: number | null,
    tab?: SeasonCampaignDetailTab | null,
): string {
    const base = Routes.manage.leasingSeason.campaigns.detail.replace(
        ':id',
        String(campaignId),
    )
    const params = new URLSearchParams()
    if (seasonId != null) {
        params.set('seasonId', String(seasonId))
    }
    if (tab && tab !== 'setup') {
        params.set('tab', tab)
    }
    const qs = params.toString()
    return qs ? `${base}?${qs}` : base
}

export function seasonCampaignsListPath(seasonId?: number | null): string {
    const base = Routes.manage.leasingSeason.campaigns.root
    if (seasonId != null) {
        return `${base}?seasonId=${seasonId}`
    }
    return base
}

export function seasonCampaignCreatePath(seasonId?: number | null): string {
    const base = Routes.manage.leasingSeason.campaigns.create
    if (seasonId != null) {
        return `${base}?seasonId=${seasonId}`
    }
    return base
}

export function leasingSeasonOutreachPath(seasonId?: number | null): string {
    const base = Routes.manage.leasingSeason.root
    if (seasonId != null) {
        return `${base}?tab=campaigns&seasonId=${seasonId}`
    }
    return `${base}?tab=campaigns`
}
